/* eslint-disable max-lines */
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FilterListPopover, MaybeTooltip } from '@modernloop/shared/components';
import { PlusIcon, TriangleDownIcon } from '@modernloop/shared/icons';
import { EmployeeId } from '@modernloop/shared/ids';
import { isValidEmail } from '@modernloop/shared/utils';
import {
  AutocompleteRenderGroupParams,
  Avatar,
  Box,
  Button,
  Checkbox,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  Theme,
  Typography,
} from '@mui/material';
import { isEmpty, uniqBy } from 'lodash';

import {
  DynamicAudience,
  EmployeeSearchInput,
  SmartEmployeeSelectFragment,
  useDynamicEmployeeQuery,
  useSmartEmployeeByIdsQuery,
  useSmartEmployeeSelectMembersLazyQuery,
} from 'src/generated/mloop-graphql';

import { EMPTY_UUID, PAGE_SIZE } from 'src/constants';

import { GetSmartEmployeeSelectChipAction, audienceChipKey, emailChipKey, employeeChipKey } from './chipAction';
import { CHIP_ROW_HEIGHT } from './chipLayout';
import { DynamicAudienceChip } from './DynamicAudienceChip';
import SmartEmployeeSelectChipEmailAddress from './SmartEmployeeSelectChipEmailAddress';
import SmartEmployeeSelectChipEmployee from './SmartEmployeeSelectChipEmployee';
import { DynamicAudienceContext } from './types';
import { useCandidateSmartOptions } from './useCandidateSmartOptions';
import { useJobSmartOptions } from './useJobSmartOptions';
import { useTaskSmartOptions } from './useTaskSmartOptions';
import { DynamicAudienceChipIconVariant, getDacIcon, getSmartOptionLabel } from './utils';

/**
 * Result of SmartEmployeeSelect onChange callback.
 * Contains all selected values.
 */
export type SmartEmployeeSelectResult = {
  /** Selected employee IDs */
  selectedEmployeeIds: EmployeeId[];
  /** Selected external email addresses */
  selectedEmailAddresses: string[];
  /** Selected dynamic audiences */
  dynamicAudiences: DynamicAudience[];
};

type Props = {
  label: string;
  placeholder: string;
  context: DynamicAudienceContext;
  onChange: (result: SmartEmployeeSelectResult) => void;
  employeeSearchInput?: Partial<EmployeeSearchInput>;
  selectedEmployeeIds?: EmployeeId[];
  selectedDynamicAudiences?: DynamicAudience[];
  selectedFreeformEmails?: string[];
  filterDynamicAudiences?: DynamicAudience[];
  footer?: React.ReactNode;
  disableMultiSelect?: boolean;
  employeeGroupLabel?: string;
  disableFreeform?: boolean;
  listHeight?: number;
  /**
   * Opt-in per-chip action. Returns `undefined` for any chip that should not carry one, so a
   * caller that passes nothing -- every non-attendee caller -- keeps today's chips exactly.
   */
  getChipAction?: GetSmartEmployeeSelectChipAction;
  /**
   * Recipients that are on the invite because an audience resolved to them. They render as their
   * own chips, each labelled with the audience that produced it, without becoming ordinary
   * selections (which would make removing the audience unable to tell its own members from explicit
   * picks).
   *
   * Passing this also selects communications mode: the audience itself gets NO chip, because every
   * one of its members already has one. Adding and removing an audience happens in the dropdown,
   * whose option carries the selected checkbox. A caller that omits this -- the settings surfaces,
   * where no application exists to resolve an audience against -- keeps the audience chip.
   */
  audienceMemberChips?: {
    members: { employeeId: EmployeeId; sourceAudience: DynamicAudience }[];
    onDelete: (employeeId: EmployeeId, sourceAudience: DynamicAudience) => void;
  };
};

const getEmployeeName = (employee: SmartEmployeeSelectFragment) =>
  employee.fullName ?? employee.givenName ?? employee.familyName ?? '';

const SmartEmployeeSelect: FC<Props> = ({
  context,
  label,
  placeholder,
  onChange,
  disableMultiSelect = false,
  selectedEmployeeIds = [],
  selectedDynamicAudiences = [],
  selectedFreeformEmails = [],
  footer,
  employeeGroupLabel,
  filterDynamicAudiences,
  listHeight = 350,
  disableFreeform = false,
  employeeSearchInput,
  getChipAction,
  audienceMemberChips,
}) => {
  const { t } = useTranslation();
  const [fetchEmployee, { data }] = useSmartEmployeeSelectMembersLazyQuery();

  const localTaskContext = typeof context.task !== 'string' && typeof context.task !== 'boolean' && !!context.task;

  const noTaskContext = !context.task || typeof context.task === 'boolean';

  const noJobContext = !context.job || typeof context.job === 'boolean';

  const localCandidateContext = typeof context.application !== 'string' && typeof context.application !== 'boolean';
  const noCandidateContext = !context.application || typeof context.application === 'boolean';

  const { data: employeesData } = useSmartEmployeeByIdsQuery({ variables: { input: selectedEmployeeIds || [] } });

  const [searchStr, setSearchStr] = useState('');

  const { data: taskdata } = useDynamicEmployeeQuery({
    variables: {
      id: `${context.task}`,
      taskId: typeof context.task === 'string' ? context.task : EMPTY_UUID,
      skipTask: localTaskContext || noTaskContext,
      skipJob: noJobContext,
      jobId: typeof context.job === 'string' ? context.job : EMPTY_UUID,
      applicationId: typeof context.application === 'string' ? context.application : EMPTY_UUID,
      skipCandidate: localCandidateContext || noCandidateContext,
    },
    fetchPolicy: 'cache-and-network',
  });

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    fetchEmployee({
      variables: {
        input: {
          pageInput: {
            offset: 0,
            limit: PAGE_SIZE,
          },
          isArchived: !searchStr ? false : undefined,
          isAtsDisabled: !searchStr ? false : undefined,
          isDirectoryDisabled: false,
          search: searchStr,
          ...employeeSearchInput,
        },
      },
      fetchPolicy: 'cache-and-network',
    });
  }, [employeeSearchInput, fetchEmployee, searchStr]);

  const {
    options: taskOptions,
    renderer: renderTaskOption,
    getData: getTaskData,
  } = useTaskSmartOptions(
    {
      taskAssigneeData: taskdata?.task?.TASK_ASSIGNEE ? [taskdata?.task?.TASK_ASSIGNEE] : [],
      taskCreatorData: taskdata?.task?.TASK_CREATOR ? [taskdata?.task?.TASK_CREATOR] : [],
      taskSubscriberData: taskdata?.task?.TASK_SUBSCRIBERS ?? [],
      taskDebriefData: taskdata?.task?.DEBRIEF_ATTENDEES ?? [],
      taskInterviewersData: [],
    },
    { task: context.task, schedule: context.schedule }
  );

  const {
    options: jobOptions,
    renderer: renderJobOption,
    getData: getJobData,
  } = useJobSmartOptions(
    {
      jobCoordinatorData: taskdata?.job?.JOB_COORDINATORS ?? [],
      jobRecruiterData: taskdata?.job?.JOB_RECRUITERS ?? [],
      jobSourcerData: taskdata?.job?.JOB_SOURCERS ?? [],
      jobHiringManagersData: taskdata?.job?.JOB_HIRING_MANAGERS ?? [],
    },
    null
  );

  const {
    options: candidateOptions,
    renderer: renderCandidateOption,
    getData: getCandidateData,
  } = useCandidateSmartOptions(
    {
      candidateCoordinatorData: taskdata?.candidate?.CANDIDATE_COORDINATOR
        ? [taskdata?.candidate?.CANDIDATE_COORDINATOR]
        : [],
      candidateRecruiterData: taskdata?.candidate?.CANDIDATE_RECRUITER
        ? [taskdata?.candidate?.CANDIDATE_RECRUITER]
        : [],
      candidateSourcerData: taskdata?.candidate?.CANDIDATE_SOURCER ? [taskdata?.candidate?.CANDIDATE_SOURCER] : [],
      previousCandidateInterviewersData: taskdata?.candidate?.CANDIDATES_PREVIOUS_INTERVIEWERS ?? [],
    },
    null
  );

  const [showPopover, setShowPopover] = useState(false);

  const sortedOptions = useMemo(() => {
    const members = uniqBy([...(data?.employeeSearch?.items ?? [])], 'id').filter((option) => option.id);

    // Sorting so that selected options come at top and disabled options come at bottom
    return (
      members.sort((a, b) => {
        if (selectedEmployeeIds?.includes(a?.id) && selectedEmployeeIds.includes(b?.id)) {
          return 0;
        }
        if (selectedEmployeeIds?.includes(a?.id)) {
          return -1;
        }
        if (selectedEmployeeIds?.includes(b?.id)) {
          return 1;
        }

        return (
          Number(Boolean(a.isArchived || a.isAtsDisabled || !a.hasAtsId)) -
          Number(Boolean(b.isArchived || b.isAtsDisabled || !b.hasAtsId))
        );
      }) ?? []
    );
  }, [data?.employeeSearch?.items, selectedEmployeeIds]);

  const getChipData = useCallback(
    (type: DynamicAudience) => {
      if (taskOptions.includes(type)) {
        return getTaskData(type);
      }
      if (candidateOptions.includes(type)) {
        return getCandidateData(type);
      }
      if (jobOptions.includes(type)) {
        return getJobData(type);
      }
      return null;
    },
    [taskOptions, candidateOptions, jobOptions, getTaskData, getCandidateData, getJobData]
  );

  type BuildResultParams = {
    employeeIds?: EmployeeId[];
    emailAddresses?: string[];
    audiences?: DynamicAudience[];
  };

  /**
   * Builds the SmartEmployeeSelectResult object with all selected values.
   */
  const buildResult = useCallback(
    ({ employeeIds, emailAddresses, audiences }: BuildResultParams): SmartEmployeeSelectResult => {
      const normalizedEmployeeIds = employeeIds ?? selectedEmployeeIds;
      return {
        selectedEmployeeIds: normalizedEmployeeIds,
        selectedEmailAddresses: emailAddresses ?? selectedFreeformEmails,
        dynamicAudiences: audiences ?? selectedDynamicAudiences,
      };
    },
    [selectedEmployeeIds, selectedFreeformEmails, selectedDynamicAudiences]
  );

  const dynamicAudienceOptions = useMemo(() => {
    const options: DynamicAudience[] = [];
    if (context.task) {
      options.push(...taskOptions);
    }
    if (context.application) {
      options.push(...candidateOptions);
    }
    if (context.job) {
      options.push(...jobOptions);
    }
    return options.map((option) => ({
      fullName: getSmartOptionLabel(option, t),
      familyName: getSmartOptionLabel(option, t),
      givenName: getSmartOptionLabel(option, t),
      email: option,
      id: option,
      isDirectoryDisabled: false,
      orgId: option,
      isAtsDisabled: false,
      hasAtsId: false,
      isArchived: false,
    }));
  }, [context.task, context.application, context.job, taskOptions, candidateOptions, jobOptions, t]);

  const getOptionDisabled = (option: SmartEmployeeSelectFragment) => {
    if (context.enableOptionsWithMissingData) {
      return false;
    }

    if (taskOptions.includes(option.id)) {
      return isEmpty(getTaskData(option.id)) || !getTaskData(option.id);
    }
    if (candidateOptions.includes(option.id)) {
      return isEmpty(getCandidateData(option.id)) || !getCandidateData(option.id);
    }
    if (jobOptions.includes(option.id)) {
      return isEmpty(getJobData(option.id)) || !getJobData(option.id);
    }
    return false;
  };

  const renderOption = (props: React.HTMLAttributes<HTMLElement>, employee: SmartEmployeeSelectFragment) => {
    if (filterDynamicAudiences?.includes(employee.id)) {
      return null;
    }
    const getRow = () => {
      if (taskOptions.includes(employee.id)) {
        return renderTaskOption({
          optionType: employee.id,
          isDisabled: getOptionDisabled(employee),
          isChecked: !!selectedDynamicAudiences?.includes(employee.id),
          disableMultiSelect,
        });
      }
      if (candidateOptions.includes(employee.id)) {
        return renderCandidateOption({
          optionType: employee.id,
          isDisabled: getOptionDisabled(employee),
          isChecked: !!selectedDynamicAudiences?.includes(employee.id),
          disableMultiSelect,
        });
      }
      if (jobOptions.includes(employee.id)) {
        return renderJobOption({
          optionType: employee.id,
          isDisabled: getOptionDisabled(employee),
          isChecked: !!selectedDynamicAudiences?.includes(employee.id),
          disableMultiSelect,
        });
      }

      return (
        <ListItemButton>
          <Stack direction="row" alignItems="center" columnGap={1} width="100%">
            {!disableMultiSelect && <Checkbox checked={selectedEmployeeIds?.includes(employee.id)} />}{' '}
            <Avatar src={employee.internalProfilePictureUrl ?? ''} alt={employee.fullName || ''} />
            <MaybeTooltip
              title={`${getEmployeeName(employee)}`}
              label={`${getEmployeeName(employee)}`}
              placement="top"
            />
          </Stack>
        </ListItemButton>
      );
    };

    return (
      <ListItemButton disabled={getOptionDisabled(employee)} {...props}>
        {getRow()}
      </ListItemButton>
    );
  };

  const isEmployeeOption = (option: { id: string }) => {
    return !dynamicAudienceOptions.find((item) => item.id === option?.id);
  };

  const handleChange = (event: React.ChangeEvent, value) => {
    if (!value) {
      return;
    }

    if (disableMultiSelect) {
      const selectedValue: { id: string }[] = [value[value.length - 1]];

      const dynamicAudiences: DynamicAudience[] = selectedValue
        .filter((option) => !isEmployeeOption(option))
        .map((option) => option.id as DynamicAudience);
      const employeeIds: EmployeeId[] = selectedValue.filter(isEmployeeOption).map((option) => option.id as EmployeeId);

      if (value.length !== 0) {
        onChange(buildResult({ employeeIds, audiences: dynamicAudiences }));
      }

      setShowPopover(false);
      return;
    }
    const dynamicAudiences: DynamicAudience[] = value
      .filter((option) => !isEmployeeOption(option))
      .map((option) => option.id);
    const employeeIds: EmployeeId[] = value.filter(isEmployeeOption).map((option) => option.id);
    onChange(buildResult({ employeeIds, audiences: dynamicAudiences }));
  };

  const renderGroup = (params: AutocompleteRenderGroupParams) => [
    <ListSubheader key={params.key}>{params.group}</ListSubheader>,
    params.children,
  ];

  const groupBy = (option) => {
    if (taskOptions.includes(option.id)) {
      return t('hiring-dashboard-common:common.smartEmployeeSelect.taskGroup');
    }
    if (candidateOptions.includes(option.id)) {
      return t('hiring-dashboard-common:common.smartEmployeeSelect.candidateGroup');
    }
    if (jobOptions.includes(option.id)) {
      return t('hiring-dashboard-common:common.smartEmployeeSelect.jobGroup');
    }
    if (selectedEmployeeIds?.includes(option.id) && !disableMultiSelect) {
      return t('hiring-dashboard-common:common.smartEmployeeSelect.selectedGroup');
    }
    return employeeGroupLabel ?? t('hiring-dashboard-common:common.select.allEmployees');
  };

  const allSelectedEmployees = useMemo(() => {
    if (selectedDynamicAudiences) {
      const findAllSelectedDynamicAudiences = dynamicAudienceOptions.filter((option) =>
        selectedDynamicAudiences.includes(option.id)
      );
      return [...(employeesData?.employeeByIds ?? []), ...findAllSelectedDynamicAudiences];
    }
    return employeesData?.employeeByIds ?? [];
  }, [dynamicAudienceOptions, employeesData?.employeeByIds, selectedDynamicAudiences]);

  const deleteDynamicAudience = (toDeleteAudience: DynamicAudience) => {
    onChange(buildResult({ audiences: selectedDynamicAudiences.filter((a) => a !== toDeleteAudience) }));
  };

  const deleteEmployee = (toDeleteId: EmployeeId) => {
    onChange(buildResult({ employeeIds: selectedEmployeeIds.filter((id) => id !== toDeleteId) }));
  };

  const deleteFreeformEmail = (toDeleteEmail: string) => {
    onChange(buildResult({ emailAddresses: selectedFreeformEmails.filter((email) => email !== toDeleteEmail) }));
  };

  const splitGroup = (type: DynamicAudience) => {
    let employees = [...(employeesData?.employeeByIds ?? [])];

    if (taskOptions.includes(type)) {
      employees = employees.concat(...(getTaskData(type) ?? []));
    }

    if (candidateOptions.includes(type)) {
      employees = employees.concat(...(getCandidateData(type) ?? []));
    }

    if (jobOptions.includes(type)) {
      employees = employees.concat(...(getJobData(type) ?? []));
    }

    const uniqueEmployees = uniqBy(employees, 'id');
    onChange(
      buildResult({
        // Add the new employees to the selected employee IDs
        employeeIds: uniqueEmployees.map((employee) => employee.id),
        // Remove the audience that was split
        audiences: selectedDynamicAudiences.filter((a) => a !== type),
      })
    );
  };

  const handleAddEmail = useCallback(() => {
    if (!disableFreeform && isValidEmail(searchStr) && searchStr.trim()) {
      const trimmedEmail = searchStr.trim();
      // Check if email is already selected
      if (!selectedFreeformEmails.includes(trimmedEmail)) {
        const updatedEmails = [...selectedFreeformEmails, trimmedEmail];
        onChange(buildResult({ emailAddresses: updatedEmails }));
      }
      setSearchStr('');
    }
  }, [disableFreeform, searchStr, selectedFreeformEmails, onChange, buildResult]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Only handle Enter key
      if (event.key === 'Enter' && !disableFreeform && isValidEmail(searchStr)) {
        event.preventDefault();
        event.stopPropagation();
        handleAddEmail();
      }
    },
    [disableFreeform, searchStr, handleAddEmail]
  );

  const getEmptyState = () => {
    if (!disableFreeform && isValidEmail(searchStr)) {
      return (
        <Stack width="100%">
          <ListItemButton onClick={handleAddEmail}>
            <Stack direction="row" alignItems="center" columnGap={1}>
              <PlusIcon />
              <ListItemText primary={searchStr} />
            </Stack>
          </ListItemButton>
        </Stack>
      );
    }

    return (
      <Stack alignItems="center" justifyContent="center" p={2}>
        <Typography color="text.secondary">{t('hiring-dashboard-common:common.noResultsFound')}</Typography>
      </Stack>
    );
  };

  const optionSelected = selectedDynamicAudiences.length || selectedEmployeeIds.length || selectedFreeformEmails.length;

  // Communications mode. The caller resolved each audience to its members, so the audience needs no
  // chip of its own: an audience that resolves to three people shows three chips, and one that
  // resolves to nobody shows none -- its selection is visible in the dropdown, which is also the
  // only place it can be dropped. De-selecting it there removes its members through the same
  // onChange path the audience chip's delete used.
  const rendersAudienceMembers = Boolean(audienceMemberChips);

  return (
    <FilterListPopover
      open={showPopover}
      values={allSelectedEmployees}
      renderOption={renderOption}
      options={[...dynamicAudienceOptions, ...(sortedOptions ?? [])]}
      onChange={handleChange}
      getOptionLabel={(option) => option.fullName ?? option.familyName ?? option.givenName ?? option.email ?? ''}
      groupBy={groupBy}
      placeholderText={t('hiring-dashboard-common:common.select.searchPeople')}
      getChildSize={() => 36}
      onPopoverClose={() => {
        setShowPopover(false);
      }}
      getOptionDisabled={(option) => getOptionDisabled(option)}
      autoFocus
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onAutoCompleteClose={() => {
        setShowPopover(false);
      }}
      onInputChange={(event: React.ChangeEvent, value: string) => {
        setSearchStr(value);
      }}
      noOptionsText={getEmptyState()}
      getListHeight={() => listHeight}
      renderGroup={renderGroup}
      footer={footer}
      onKeyDown={handleKeyDown}
    >
      {disableMultiSelect ? (
        <Stack data-testid="smart-employee-select-trigger" onClick={() => setShowPopover(true)}>
          <Button
            fullWidth
            onClick={() => setShowPopover(true)}
            endIcon={
              <TriangleDownIcon
                sx={{
                  transform: () => (showPopover ? `rotate(180deg)` : `rotate(0deg)`),
                }}
              />
            }
            sx={{
              borderRadius: '6px',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            {optionSelected ? (
              <Box>
                {selectedDynamicAudiences &&
                  [selectedDynamicAudiences[0]]?.map((audience) => {
                    if (
                      taskOptions.includes(audience) ||
                      candidateOptions.includes(audience) ||
                      jobOptions.includes(audience)
                    ) {
                      return (
                        <Stack key={audience} direction="row" alignItems="center" columnGap={1} width="100%">
                          <ListItemIcon
                            sx={{
                              color: (theme: Theme) => theme.palette.primary.main,
                            }}
                          >
                            {getDacIcon(audience, DynamicAudienceChipIconVariant.Primary)}{' '}
                          </ListItemIcon>
                          <MaybeTooltip
                            title={`${getSmartOptionLabel(audience, t)}`}
                            label={`${getSmartOptionLabel(audience, t)}`}
                            placement="top"
                          />
                        </Stack>
                      );
                    }

                    return null;
                  })}
                {employeesData?.employeeByIds?.map((employee) => {
                  return (
                    <Stack key={employee.id} direction="row" alignItems="center" columnGap={1} width="100%">
                      <Avatar src={employee.internalProfilePictureUrl ?? ''} alt={employee.fullName || ''} />
                      <MaybeTooltip
                        title={`${getEmployeeName(employee)}`}
                        label={`${getEmployeeName(employee)}`}
                        placement="top"
                      />
                    </Stack>
                  );
                })}
              </Box>
            ) : (
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                {t('hiring-dashboard-common:common.select.label')}
              </Stack>
            )}
          </Button>
        </Stack>
      ) : (
        <Stack
          data-testid="smart-employee-select-trigger"
          borderRadius={2}
          p={1}
          border={(theme: Theme) => `1px solid ${theme.palette.borderState.default}`}
          onClick={() => {
            setShowPopover(true);
          }}
          sx={{
            backgroundColor: (theme: Theme) => theme.palette.background.default,
          }}
        >
          <Typography color="text.secondary" variant="subtitle2">
            {label}
          </Typography>
          <Stack
            gap={1}
            flexWrap="wrap"
            direction="row"
            alignItems="center"
            sx={{
              cursor: 'pointer',
            }}
          >
            {!rendersAudienceMembers &&
              selectedDynamicAudiences?.map((audience) => {
                return (
                  <DynamicAudienceChip
                    key={audience}
                    onDelete={deleteDynamicAudience}
                    dynamicAudience={audience}
                    renderData={getChipData(audience)}
                    context={context}
                    onSplit={splitGroup}
                    action={getChipAction?.(audienceChipKey(audience))}
                  />
                );
              })}
            {audienceMemberChips?.members?.map((member) => (
              <SmartEmployeeSelectChipEmployee
                key={`${member.sourceAudience}:${member.employeeId}`}
                id={member.employeeId}
                sourceAudience={member.sourceAudience}
                onDelete={() => audienceMemberChips.onDelete(member.employeeId, member.sourceAudience)}
                action={getChipAction?.(employeeChipKey(member.employeeId))}
              />
            ))}
            {selectedEmployeeIds?.map((id) => (
              <SmartEmployeeSelectChipEmployee
                key={id}
                id={id as EmployeeId}
                onDelete={() => deleteEmployee(id as EmployeeId)}
                action={getChipAction?.(employeeChipKey(id))}
              />
            ))}
            {selectedFreeformEmails?.map((email) => (
              <SmartEmployeeSelectChipEmailAddress
                key={email}
                email={email}
                onDelete={() => deleteFreeformEmail(email)}
                action={getChipAction?.(emailChipKey(email))}
              />
            ))}
            {/* Same row height as a chip, so the inline input sits on the cluster's baseline. */}
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', height: `${CHIP_ROW_HEIGHT}px` }}
            >
              {placeholder}
            </Typography>
          </Stack>
        </Stack>
      )}
    </FilterListPopover>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MemberQuery = gql`
  ${useTaskSmartOptions.fragments.taskAssigneeData}
  ${useTaskSmartOptions.fragments.taskCreatorData}
  ${useTaskSmartOptions.fragments.taskDebriefData}
  ${useTaskSmartOptions.fragments.taskSubscriberData}
  ${useTaskSmartOptions.fragments.taskInterviewersData}
  ${useJobSmartOptions.fragments.jobCoordinatorData}
  ${useJobSmartOptions.fragments.jobRecruiterData}
  ${useJobSmartOptions.fragments.jobSourcerData}
  ${useJobSmartOptions.fragments.jobHiringManagersData}
  ${useCandidateSmartOptions.fragments.candidateCoordinatorData}
  ${useCandidateSmartOptions.fragments.candidateRecruiterData}
  ${useCandidateSmartOptions.fragments.candidateSourcerData}
  ${useCandidateSmartOptions.fragments.previousCandidateInterviewersData}
  fragment SmartEmployeeSelect on Employee {
    id
    fullName
    givenName
    familyName
    internalProfilePictureUrl
    email
    isArchived
    isAtsDisabled
    hasAtsId
    isDisabled
    orgId
  }
  query SmartEmployeeSelectMembers($input: EmployeeSearchInput!) {
    employeeSearch(input: $input) {
      items {
        ...SmartEmployeeSelect
      }
    }
  }
  query SmartEmployeeByIds($input: [uuid!]!) {
    employeeByIds(ids: $input) {
      ...SmartEmployeeSelect
    }
  }
  query SmartEmployeeGetInterviewersOnPlanByIds($taskInterviewersOnScheduleEmployeeIds: [uuid!]!) {
    employeeByIds(ids: $taskInterviewersOnScheduleEmployeeIds) {
      ...useTaskSmartOptions_taskInterviewersData
    }
  }
  query DynamicEmployee(
    $id: String!
    $taskId: uuid!
    $jobId: uuid!
    $applicationId: uuid!
    $skipTask: Boolean!
    $skipJob: Boolean!
    $skipCandidate: Boolean!
  ) {
    task: dynamicEmployee(id: $id) @skip(if: $skipTask) {
      TASK_ASSIGNEE(input: { taskId: $taskId }) {
        ...useTaskSmartOptions_taskAssigneeData
      }
      TASK_SUBSCRIBERS(input: { taskId: $taskId }) {
        ...useTaskSmartOptions_taskSubscriberData
      }
      TASK_CREATOR(input: { taskId: $taskId }) {
        ...useTaskSmartOptions_taskCreatorData
      }
      DEBRIEF_ATTENDEES(input: { taskId: $taskId }) {
        ...useTaskSmartOptions_taskDebriefData
      }
      # INTERVIEWERS_ON_SCHEDULE(input: { employeeIds: $employeeIds }) {
      #   ...OptionRenderer_employees
      # }
    }
    job: dynamicEmployee(id: $id) @skip(if: $skipJob) {
      JOB_COORDINATORS(input: { jobId: $jobId }) {
        ...useJobSmartOptions_jobCoordinatorData
      }
      JOB_RECRUITERS(input: { jobId: $jobId }) {
        ...useJobSmartOptions_jobRecruiterData
      }
      JOB_SOURCERS(input: { jobId: $jobId }) {
        ...useJobSmartOptions_jobSourcerData
      }
      JOB_HIRING_MANAGERS(input: { jobId: $jobId }) {
        ...useJobSmartOptions_jobHiringManagersData
      }
    }
    candidate: dynamicEmployee(id: $id) @skip(if: $skipCandidate) {
      CANDIDATE_COORDINATOR(input: { applicationId: $applicationId }) {
        ...useCandidateSmartOptions_candidateCoordinatorData
      }
      CANDIDATE_RECRUITER(input: { applicationId: $applicationId }) {
        ...useCandidateSmartOptions_candidateRecruiterData
      }
      CANDIDATE_SOURCER(input: { applicationId: $applicationId }) {
        ...useCandidateSmartOptions_candidateSourcerData
      }
      CANDIDATES_PREVIOUS_INTERVIEWERS(input: { applicationId: $applicationId }) {
        ...useCandidateSmartOptions_previousCandidateInterviewersData
      }
    }
  }
`;

export default SmartEmployeeSelect;
